import { FormEvent, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { LoadingState } from '@/components/states';
import { useAuth } from '@/auth/AuthProvider';

interface AuthGateProps {
  children: ReactNode;
}

export function AuthGate({ children }: AuthGateProps) {
  const { t } = useTranslation();
  const auth = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (auth.isLoading) return <LoadingState label={t('auth.loading')} />;
  if (auth.session) return <>{children}</>;

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      await auth.signIn(email, password);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('auth.failed'));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="max-w-md mx-auto mt-12 border border-border rounded-lg bg-surface-2 p-6">
      <div className="text-xs font-medium text-accent mb-2">
        {t('auth.title')}
      </div>
      <h1 className="font-sans text-xl font-semibold tracking-[-0.015em] mb-2">{t('auth.heading')}</h1>
      <p className="text-sm text-ink-3 mb-6">{t('auth.body')}</p>

      {!auth.isConfigured && (
        <div className="mb-5 border border-danger/40 bg-danger/5 rounded p-3 text-sm text-danger">
          {t('auth.missing_config')}
        </div>
      )}

      <form onSubmit={submit} className="space-y-4">
        <label className="block">
          <span className="block text-xs font-medium text-ink-2 mb-1">
            {t('auth.email')}
          </span>
          <input
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            disabled={!auth.isConfigured || isSubmitting}
            className="w-full bg-surface-3 border border-border-2 rounded px-3 py-2 text-ink
                       focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus:border-accent"
            required
          />
        </label>

        <label className="block">
          <span className="block text-xs font-medium text-ink-2 mb-1">
            {t('auth.password')}
          </span>
          <input
            type={auth.isMock ? 'text' : 'password'}
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            disabled={!auth.isConfigured || isSubmitting}
            className="w-full bg-surface-3 border border-border-2 rounded px-3 py-2 text-ink
                       focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus:border-accent"
            required
          />
        </label>

        {error && <div className="text-sm text-danger">{error}</div>}

        <button
          type="submit"
          disabled={!auth.isConfigured || isSubmitting}
          className="w-full text-xs font-medium px-4 py-2.5 rounded
                     bg-accent/15 text-accent border border-accent/40
                     hover:bg-accent/25 disabled:opacity-50 disabled:cursor-not-allowed
                     transition-colors"
        >
          {isSubmitting ? t('auth.signing_in') : t('auth.sign_in')}
        </button>
      </form>
    </div>
  );
}
